import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { AssistantUiContext, type AssistantUi } from "./assistant-ui-context";

/**
 * Holds the assistant panel's open state and the global ⌘K / Ctrl-K shortcut
 * (Escape closes). Mounted once at the shell level. The context + `useAssistantUi`
 * hook live in `assistant-ui-context.ts` (component-only exports here).
 */
export function AssistantUiProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const value = useMemo<AssistantUi>(
    () => ({
      open,
      openPanel: () => setOpen(true),
      closePanel: () => setOpen(false),
      toggle: () => setOpen((o) => !o),
    }),
    [open],
  );

  return <AssistantUiContext.Provider value={value}>{children}</AssistantUiContext.Provider>;
}
